import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { RecommendedBadge } from '../../common/RecommendedBadge';
import { Tooltip } from '../../common/Tooltip';
import { ChevronIcon } from '../../common/selectIcons';
import type { GenerationModel } from '../../../features/generation-models/api';
import { GENERATION_MODEL_SPEED } from '../state/modelCatalog';
import {
  gptImageQuality,
  groupGptImageModelsBySection,
  isGptImageModelCode,
  stripGptPrefix,
} from '../utils/imageModelFamily';
import msStyles from '../../common/MultiSelectDropdown.module.css';
import styles from './ImageModelFamilySelector.module.css';

interface ImageModelFamilySelectorProps {
  models: GenerationModel[];
  activeModelCode: string;
  onSelect: (modelCode: string) => void;
  /** 指定時、このセットに含まれない model_code は選べない (品質メニューでは disabled 表示)。
   *  undefined ならすべて有効。ストーリーボードモード等で「自動のみ選択可」表示に使う。 */
  enabledModelCodes?: Set<string>;
  /** Gemini の分割ボタンの右隣に置く補助設定 (例: 思考レベルの plain セレクト)。
   *  選択中のモデルにだけある細かい設定を、モデル選択と同じ行に並べる。 */
  geminiTrailing?: ReactNode;
}

/**
 * 画像生成モデル選択 UI。3 階層構成 (2026-09-08 ユーザー指定・09-09 に Nano-banana も同構造化):
 *
 *   セクション (プロバイダ / バージョン)  →  モデル (ファミリー) ボタン  →  品質 (シェブロンのドロップダウン)
 *   GPT Image 2.5                           標準 · 自動 (Flare)           自動★ / 最高品質 / 超高品質 / 高品質 / 標準 / 低品質
 *                                           長時間 · 自動 (Sunburst)      自動 / 最高品質 / 超高品質 / 高品質
 *   Google Gemini                           標準 (品質名のみ)             高品質 / 標準 / スピード
 *
 * - モデルボタンの本体クリック = そのファミリーを選択 (選択中なら現在の品質、未選択なら既定品質)。
 *   ボタンには常に品質を表示する (選択中なら現在の品質、未選択なら本体クリックで選ばれる既定品質)。
 *   シェブロンで品質メニューを開き、行をクリックすると model_code を onSelect する。
 *   品質メニューに「既定」タグは付けない (2026-09-09 ユーザー指示)。
 * - GPT 系のボタン表示名は通称 (標準 / 長時間。imageModelFamily.GPT_IMAGE_FAMILY_ALIASES) + 品質。
 *   通称を出しているボタンはカスタムツールチップで正式名称 (generation_models.model_family_name) を出す。
 *   Gemini は 1 ファミリーしか無いので、ボタンは品質名 (高品質 / 標準 / スピード) だけを出す。
 * - 品質の並び・既定 (is_default) ・推奨 (is_recommended) は generation_models 駆動。
 *   Gemini は is_default 行が無いため GENERATION_MODEL_SPEED (標準) を既定品質にする。
 *
 * 静止画の媒体要件 ([MediaRequirementsAccordion]) とシナリオ生成モードの画像生成モデル切替で
 * 共有される。状態更新ロジック (localStorage 連動や image_size との連動) は呼び出し側が onSelect で吸収する。
 */
export function ImageModelFamilySelector({
  models,
  activeModelCode,
  onSelect,
  enabledModelCodes,
  geminiTrailing,
}: ImageModelFamilySelectorProps) {
  const geminiModels = models
    .filter((m) => !isGptImageModelCode(m.model_code))
    .sort((a, b) => a.sort_order - b.sort_order);
  const gptSections = groupGptImageModelsBySection(models);
  const isEnabled = (code: string) => enabledModelCodes === undefined || enabledModelCodes.has(code);

  return (
    <>
      {gptSections.map((section) => (
        <div key={section.key} className={styles.modelFamilyGroup} data-section={section.key}>
          <div className={styles.modelFamilyHeader}>
            <span
              className={styles.modelFamilyIconMask}
              role="img"
              aria-label="OpenAI"
              style={{
                WebkitMaskImage: 'url("/openai_icon.svg")',
                maskImage: 'url("/openai_icon.svg")',
              }}
            />
            <span>{section.label}</span>
          </div>
          <div className={styles.modelQualityRow}>
            {section.families.map((family) => (
              <FamilySplitButton
                key={family.key}
                familyKey={family.key}
                label={family.label}
                officialLabel={family.officialLabel}
                tooltip={family.tooltip}
                options={family.list}
                // display_label から "GPT" プレフィックスを落とし品質のみ見せる (GPT標準 → 標準)
                qualityLabel={(m) => stripGptPrefix(m.display_label)}
                activeModelCode={activeModelCode}
                onSelect={onSelect}
                isEnabled={isEnabled}
              />
            ))}
          </div>
        </div>
      ))}
      {geminiModels.length > 0 && (
        <div className={styles.modelFamilyGroup} data-section="gemini">
          <div className={styles.modelFamilyHeader}>
            <span className={styles.modelFamilyIcon} role="img" aria-label="banana">
              🍌
            </span>
            <span>Google Gemini</span>
          </div>
          <div className={styles.modelQualityRow}>
            <FamilySplitButton
              familyKey="gemini"
              officialLabel="Gemini"
              tooltip={null}
              options={geminiModels}
              qualityLabel={(m) => m.display_label}
              defaultModelCode={GENERATION_MODEL_SPEED}
              activeModelCode={activeModelCode}
              onSelect={onSelect}
              isEnabled={isEnabled}
            />
            {geminiTrailing && <div className={styles.modelTrailing}>{geminiTrailing}</div>}
          </div>
        </div>
      )}
    </>
  );
}

interface FamilySplitButtonProps {
  familyKey: string;
  /** ボタン本体の表示名 (通称があれば通称)。省略時は品質名だけを表示する (Gemini) */
  label?: string;
  /** 正式名称。品質メニュー / シェブロンの aria-label に使う */
  officialLabel: string;
  /** 通称を表示しているときに添える正式名称のツールチップ。無ければ null */
  tooltip: string | null;
  /** ファミリー内のモデル行 (品質メニューの並び順) */
  options: GenerationModel[];
  /** 品質メニュー / 選択中表示に出す品質名 */
  qualityLabel: (m: GenerationModel) => string;
  /** is_default 行が無いファミリーの既定品質 (model_code)。省略時は quality=auto → 先頭 */
  defaultModelCode?: string;
  activeModelCode: string;
  onSelect: (modelCode: string) => void;
  isEnabled: (modelCode: string) => boolean;
}

/** 既定品質の行。generation_models.is_default → defaultModelCode → quality=auto → 先頭 の順で決める。 */
function resolveDefaultModel(list: GenerationModel[], defaultModelCode?: string): GenerationModel | undefined {
  return (
    list.find((m) => m.is_default)
    ?? (defaultModelCode ? list.find((m) => m.model_code === defaultModelCode) : undefined)
    ?? list.find((m) => gptImageQuality(m.model_code) === 'auto')
    ?? list[0]
  );
}

/**
 * 1 ファミリー = 1 つの分割ボタン。左 (本体) でファミリー選択、右 (シェブロン) で品質メニュー。
 * 見た目は 1 つのピルで、区切り線は引かない (シェブロンだけがホバーで明るくなる)。
 * 本体には「ファミリー名 · 品質」(label あり) または「品質」(label なし) を常に表示する。
 */
function FamilySplitButton({
  familyKey,
  label,
  officialLabel,
  tooltip,
  options,
  qualityLabel,
  defaultModelCode,
  activeModelCode,
  onSelect,
  isEnabled,
}: FamilySplitButtonProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, minWidth: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const activeModel = options.find((m) => m.model_code === activeModelCode);
  const isActive = activeModel !== undefined;
  const defaultModel = resolveDefaultModel(options, defaultModelCode);
  const preferred = activeModel ?? defaultModel;
  // 本体クリックで選ぶ行: 選択中ならその品質、未選択なら既定品質。許可外なら許可された先頭。
  const mainTarget = preferred && isEnabled(preferred.model_code)
    ? preferred
    : options.find((m) => isEnabled(m.model_code));
  const isRecommended = options.some((m) => m.is_recommended);

  const updatePosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // 常時表示する品質: 選択中ならその品質、未選択なら本体クリックで選ばれる行の品質
  const shownQuality = mainTarget ?? preferred;
  const mainButton = (
    <button
      type="button"
      className={styles.splitMain}
      onClick={() => {
        if (mainTarget) onSelect(mainTarget.model_code);
      }}
      disabled={!mainTarget}
      aria-pressed={isActive}
    >
      {isRecommended && <RecommendedBadge active={isActive} />}
      {label && <span>{label}</span>}
      {shownQuality && (
        <span className={label ? styles.splitQuality : undefined}>{qualityLabel(shownQuality)}</span>
      )}
    </button>
  );

  return (
    <div
      ref={containerRef}
      className={`${styles.splitButton} ${isActive ? styles.splitButtonActive : ''}`}
      data-family={familyKey}
    >
      {tooltip ? (
        <Tooltip content={tooltip} placement="bottom" className={styles.splitMainTooltip}>
          {mainButton}
        </Tooltip>
      ) : (
        mainButton
      )}
      <button
        type="button"
        className={styles.splitChevron}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label ?? officialLabel} の品質を選択`}
      >
        <ChevronIcon open={open} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className={`${msStyles.dropdown} modal-surface ${styles.qualityMenu}`}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, minWidth: menuPos.minWidth }}
          role="listbox"
          aria-label={`${officialLabel} の品質`}
        >
          {options.map((m) => {
            const selected = m.model_code === activeModelCode;
            const enabled = isEnabled(m.model_code);
            return (
              <div
                key={m.model_code}
                role="option"
                aria-selected={selected}
                aria-disabled={!enabled}
                className={`${msStyles.option} ${styles.qualityOption} ${selected ? msStyles.optionSelected : ''} ${enabled ? '' : styles.qualityOptionDisabled}`}
                onClick={() => {
                  if (!enabled) return;
                  onSelect(m.model_code);
                  setOpen(false);
                }}
              >
                {m.is_recommended && <RecommendedBadge active={selected} />}
                <span>{qualityLabel(m)}</span>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
